import {messages} from '@smartmixin/i18n';
import {StatusCodes} from 'http-status-codes';
import React from 'react';
import {IntlShape, MessageDescriptor, useIntl} from 'react-intl';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {AuthService, UserService, getApiUrl} from '../api';
import {
  PasskeyAssertion,
  PasskeyRequestOptions,
  StaffElevationResponse,
  UserDetail,
} from '../api/types';
import {
  clearStaffElevation,
  getDebugEmail,
  selectStaffElevation,
  setStaffElevation,
} from '../store';
import {CommonRootState} from '../store/types';
import {Dates} from '../utils';
import {subscribeToNothing, subscribeToSeconds} from './clockSubscription';
import useApiClient, {ApiError} from './useApiClient';

/** The 403 `detail.code` for a staff user with no TOTP / recovery codes. */
const MFA_NOT_ENROLLED = 'mfa_not_enrolled';
/** The 403 `detail.code` for a staff user with a code factor but no passkey. */
const NO_PASSKEY = 'no_passkey';
/** The 400 `credential` code when the passkey challenge is gone. */
const CHALLENGE_EXPIRED = 'challenge_expired';
/** The 400 `credential` code when the per-user attempt budget is spent. */
const TOO_MANY_ATTEMPTS = 'too_many_login_attempts';

/**
 * Where an admin without a code-producing factor adds one: the back office.
 * Null when the API root is not an absolute URL -- the webapp's dev server
 * runs with an empty root (relative `/api`, proxied by Vite), where `/bo/...`
 * would resolve against the dev server and 404.
 */
const backOfficeUrl = (path: string) => {
  const root = getApiUrl();
  return /^https?:\/\//.test(root) ? `${root}${path}` : null;
};
const mfaSetupUrl = () => backOfficeUrl('/bo/account/setup/');
/** Opened in the phone's browser, it saves the passkey on that phone. */
const passkeySetupUrl = () => backOfficeUrl('/bo/account/2fa/webauthn/add/');

export type StaffElevationError = {
  /** Ready to display. */
  message: string;
  /** Set only when the admin has no usable factor enrolled yet. */
  setupUrl: string | null;
  /** The link text for `setupUrl`, null with it. */
  setupLabel: string | null;
  /** Which control failed, so the screen shows the error next to it. */
  source: 'code' | 'passkey';
};

/**
 * Why the OS gave no assertion. Each app maps its native errors to these:
 * core decides what to say, the app knows what its library throws.
 */
export type PasskeyFailureKind =
  /** The person dismissed the OS sheet: not an error. */
  | 'cancelled'
  /** No passkey for this account on this device. */
  | 'noCredential'
  /** The device or OS cannot do passkeys at all. */
  | 'notSupported'
  /** The app or the server's association files are wrong (a bug). */
  | 'misconfigured'
  | 'unknown';

export class PasskeyFailure extends Error {
  readonly kind: PasskeyFailureKind;

  constructor(kind: PasskeyFailureKind, message?: string) {
    super(message ?? kind);
    this.name = 'PasskeyFailure';
    this.kind = kind;
  }
}

/** Asks the OS for an assertion; rejects with a `PasskeyFailure`. */
export type GetPasskeyAssertion = (
  options: PasskeyRequestOptions,
) => Promise<PasskeyAssertion>;

type Fetch = ReturnType<typeof useApiClient>['fetch'];
type EditUserDetails = (data: UserDetail) => void;
type Options = {
  /** Only from an app on a device that can use passkeys. */
  getPasskeyAssertion?: GetPasskeyAssertion;
};

/** The back-office page that fixes the failure, if there is one. */
const setupLink = (
  intl: IntlShape,
  url: string | null,
  label: MessageDescriptor,
) => ({setupUrl: url, setupLabel: url ? intl.formatMessage(label) : null});

/** Failures either control can hit: no second factor at all, offline, … */
const describeCommon = (
  intl: IntlShape,
  reason: unknown,
  source: StaffElevationError['source'],
): StaffElevationError => {
  if (reason instanceof ApiError) {
    if (
      reason.statusCode === StatusCodes.FORBIDDEN &&
      reason.details?.code === MFA_NOT_ENROLLED
    ) {
      return {
        message: intl.formatMessage(messages.auth.staffVerificationNotEnrolled),
        ...setupLink(
          intl,
          mfaSetupUrl(),
          messages.auth.staffVerificationSetupLink,
        ),
        source,
      };
    }
    if (reason.details?.message) {
      return {
        message: reason.details.message,
        setupUrl: null,
        setupLabel: null,
        source,
      };
    }
  }
  return {
    message: intl.formatMessage(messages.network.unknownError),
    setupUrl: null,
    setupLabel: null,
    source,
  };
};

/**
 * Whole minutes left, rounded UP so the last minute reads "1 min" and 0 means
 * expired. Minutes, not seconds, so the clock snapshot below changes once a
 * minute and the screen re-renders at that rate rather than every second.
 */
const minutesUntil = (expiresAt: number) =>
  Math.max(0, Math.ceil((expiresAt - Date.now() / 1000) / 60));

/**
 * The Debug screens' view of the staff second factor.
 *
 * `fetch` and `editUserDetails` are the screen's own `useApiClient().fetch`
 * and `useUserEdit()`, passed in rather than created here so each app's tests
 * drive this hook through the same mocks as the rest of the screen.
 *
 * Once elevated, a Login As that the backend refused for want of a code is
 * completed: the target's details are fetched once, exactly as the Login As
 * button does. Nothing is retried automatically beyond that.
 *
 * Nothing here decides WHEN to ask for a code: `required` is raised by
 * useApiClient on the backend's `staff_elevation_required` 403 and by nothing
 * else, so the prompt cannot appear before the backend enforces the factor.
 *
 * With `getPasskeyAssertion`, `submitPasskey` gets the same token from a
 * passkey: the app wraps its native passkey API, core does the exchange and
 * says what went wrong.
 */
const useStaffElevation = (
  fetch: Fetch,
  editUserDetails: EditUserDetails,
  {getPasskeyAssertion}: Options = {},
) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const store = useStore<CommonRootState>();
  // A ref, not the `submitting` state: two submits in the same tick (the
  // keyboard's submit key bypasses the disabled button) both see the state
  // before React re-renders. A TOTP sent twice is a replay 400 that also
  // spends the backend's rate limit. Shared by the code and the passkey, which
  // spend the same budget.
  const inFlightRef = React.useRef(false);
  const {token, expiresAt, required} = useSelector(selectStaffElevation);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<StaffElevationError | null>(null);

  const holdsToken = token !== null && expiresAt !== null;
  const minutesLeft = React.useSyncExternalStore(
    holdsToken ? subscribeToSeconds : subscribeToNothing,
    () =>
      token !== null && expiresAt !== null ? minutesUntil(expiresAt) : null,
  );

  React.useEffect(() => {
    if (minutesLeft === 0) {
      dispatch(clearStaffElevation());
    }
  }, [minutesLeft, dispatch]);

  const describeCode = React.useCallback(
    (reason: unknown): StaffElevationError => {
      if (
        reason instanceof ApiError &&
        reason.statusCode === StatusCodes.BAD_REQUEST
      ) {
        // Wrong, reused or rate-limited code: the backend's own message
        // for the `code` field, which says which of those it was.
        return {
          message:
            reason.fields.code?.message ||
            intl.formatMessage(messages.auth.staffVerificationInvalidCode),
          setupUrl: null,
          setupLabel: null,
          source: 'code',
        };
      }
      return describeCommon(intl, reason, 'code');
    },
    [intl],
  );

  const describePasskey = React.useCallback(
    (reason: unknown): StaffElevationError => {
      const passkey = (
        message: MessageDescriptor,
        setupUrl: string | null = null,
      ): StaffElevationError => ({
        message: intl.formatMessage(message),
        ...setupLink(
          intl,
          setupUrl,
          messages.auth.staffVerificationPasskeySetupLink,
        ),
        source: 'passkey',
      });
      if (reason instanceof PasskeyFailure) {
        switch (reason.kind) {
          case 'noCredential':
            return passkey(
              messages.auth.staffVerificationPasskeyNotEnrolled,
              passkeySetupUrl(),
            );
          case 'notSupported':
          case 'misconfigured':
            return passkey(messages.auth.staffVerificationPasskeyUnavailable);
          default:
            return passkey(messages.auth.staffVerificationPasskeyFailed);
        }
      }
      if (reason instanceof ApiError) {
        if (reason.statusCode === StatusCodes.BAD_REQUEST) {
          const field = reason.fields.credential;
          if (field?.code === CHALLENGE_EXPIRED) {
            return passkey(messages.auth.staffVerificationPasskeyExpired);
          }
          if (field?.code === TOO_MANY_ATTEMPTS && field.message) {
            return {
              message: field.message,
              setupUrl: null,
              setupLabel: null,
              source: 'passkey',
            };
          }
          return passkey(messages.auth.staffVerificationPasskeyFailed);
        }
        if (
          reason.statusCode === StatusCodes.FORBIDDEN &&
          reason.details?.code === NO_PASSKEY
        ) {
          return passkey(
            messages.auth.staffVerificationPasskeyNotEnrolled,
            passkeySetupUrl(),
          );
        }
        // A server from before passkeys (the Debug screen can point at any).
        if (reason.statusCode === StatusCodes.NOT_FOUND) {
          return passkey(messages.auth.staffVerificationPasskeyUnavailable);
        }
      }
      return describeCommon(intl, reason, 'passkey');
    },
    [intl],
  );

  /**
   * Runs one elevation attempt, code or passkey, and stores the token.
   * Callers take `inFlightRef` first, in the same tick as the press. Resolves
   * false, with `error` set unless `describeFailure` returns null, on failure.
   */
  const attempt = React.useCallback(
    async (
      exchange: () => Promise<StaffElevationResponse>,
      describeFailure: (reason: unknown) => StaffElevationError | null,
    ) => {
      inFlightRef.current = true;
      setSubmitting(true);
      setError(null);
      try {
        const res = await exchange();
        dispatch(
          setStaffElevation({
            token: res.elevation_token,
            expiresAt: res.expires_at,
          }),
        );
        if (getDebugEmail(store.getState())) {
          // Not awaited: the elevation succeeded whatever this returns.
          fetch(UserService.getUserDetail())
            .then((data) => {
              editUserDetails(data);
            })
            .catch(() => {});
        }
        return true;
      } catch (reason) {
        setError(describeFailure(reason));
        return false;
      } finally {
        inFlightRef.current = false;
        setSubmitting(false);
      }
    },
    [fetch, dispatch, store, editUserDetails],
  );

  const submit = React.useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed || inFlightRef.current) {
        return false;
      }
      return attempt(
        () =>
          fetch(AuthService.elevate(trimmed), {
            skipLoginAs: true,
            // Every failure is shown inline next to the code field.
            silent: true,
          }),
        describeCode,
      );
    },
    [fetch, attempt, describeCode],
  );

  const submitPasskey = React.useCallback(async () => {
    if (!getPasskeyAssertion || inFlightRef.current) {
      return false;
    }
    return attempt(
      async () => {
        const {options} = await fetch(AuthService.elevatePasskeyBegin(), {
          skipLoginAs: true,
          silent: true,
        });
        const assertion = await getPasskeyAssertion(options);
        return fetch(AuthService.elevatePasskeyComplete(assertion), {
          skipLoginAs: true,
          silent: true,
        });
      },
      (reason) =>
        // Dismissing the OS sheet is a choice, not a failure.
        reason instanceof PasskeyFailure && reason.kind === 'cancelled'
          ? null
          : describePasskey(reason),
    );
  }, [fetch, attempt, describePasskey, getPasskeyAssertion]);

  const elevated = minutesLeft !== null && minutesLeft > 0;

  return {
    /** Show the code prompt. */
    required: required && !elevated,
    elevated,
    /** e.g. "Verified. Expires in 11 hr 59 min." -- null unless elevated. */
    expiresInLabel: elevated
      ? intl.formatMessage(messages.auth.staffVerificationExpiresIn, {
          duration: Dates.formatDuration(minutesLeft * 60, intl),
        })
      : null,
    submitting,
    error,
    /** Resolves true once elevated; false (with `error` set) otherwise. */
    submit,
    /** Whether `submitPasskey` can do anything on this device. */
    passkeyAvailable: getPasskeyAssertion !== undefined,
    /**
     * Resolves true once elevated; false otherwise, with `error` set unless
     * the person cancelled the OS prompt.
     */
    submitPasskey,
  };
};

export default useStaffElevation;
